import React, { useEffect, useState, useCallback } from "react";
import UserAvatar from "@/components/UserAvatar";
import api, { formatApiError } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ScrollText, Search, ChevronLeft, ChevronRight, X,
  Building2, GraduationCap, Users, BookOpen, Briefcase, KeyRound,
  Receipt, Wallet, ClipboardList, LogIn, ShieldCheck, AlertCircle,
} from "lucide-react";

const PAGE_SIZE = 25;

// Module → tinted pill style + lucide icon
const MODULE_STYLES = {
  auth:           { label: "Auth",            tone: "bg-[#F3F4F6] text-[#475569]", icon: LogIn },
  schools:        { label: "Schools",         tone: "bg-[#DBEAFE] text-[#1D4ED8]", icon: GraduationCap },
  branches:       { label: "Branches",        tone: "bg-[#FFE8D6] text-[#D97706]", icon: Building2 },
  students:       { label: "Students",        tone: "bg-[#EDE9FE] text-[#7C3AED]", icon: Users },
  classes:        { label: "Classes",         tone: "bg-[#DBEAFE] text-[#1D4ED8]", icon: BookOpen },
  sections:       { label: "Sections",        tone: "bg-[#E0F2FE] text-[#0369A1]", icon: BookOpen },
  employees:      { label: "Employees",       tone: "bg-[#FCE7F3] text-[#BE185D]", icon: Briefcase },
  branch_admins:  { label: "Branch Admins",   tone: "bg-[#FEF3C7] text-[#92400E]", icon: ShieldCheck },
  special_access: { label: "Special Access",  tone: "bg-[#F3E8FF] text-[#7E22CE]", icon: KeyRound },
  payslip:        { label: "Pay Slip",        tone: "bg-[#DCFCE7] text-[#15803D]", icon: Receipt },
  accounting:     { label: "Accounting",      tone: "bg-[#FEF3C7] text-[#92400E]", icon: Wallet },
  tuition:        { label: "Tuition",         tone: "bg-[#DCFCE7] text-[#15803D]", icon: Wallet },
  events:         { label: "Events",          tone: "bg-[#FFE4E6] text-[#BE123C]", icon: ClipboardList },
  inventory:      { label: "Inventory",       tone: "bg-[#E0F2FE] text-[#0369A1]", icon: ClipboardList },
};

const ACTION_OPTIONS = [
  "all", "login", "created", "updated", "deleted",
  "approved", "rejected", "resubmitted",
  "status_changed", "granted", "revoked",
  "paid",
  "income_created", "income_updated", "income_deleted",
  "expense_created", "expense_updated", "expense_deleted",
  "payment_recorded", "payment_reversed",
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function ModulePill({ module }) {
  const style = MODULE_STYLES[module] || { label: module, tone: "bg-slate-100 text-slate-600", icon: ScrollText };
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-heading font-semibold ${style.tone}`}>
      <Icon className="w-3 h-3" /> {style.label}
    </span>
  );
}

function ActionBadge({ action }) {
  const palette = {
    login: "bg-slate-100 text-slate-600",
    created: "bg-[#DCFCE7] text-[#15803D]",
    updated: "bg-[#DBEAFE] text-[#1D4ED8]",
    deleted: "bg-[#FFE4E6] text-[#BE123C]",
    approved: "bg-[#DCFCE7] text-[#15803D]",
    rejected: "bg-[#FFE4E6] text-[#BE123C]",
    resubmitted: "bg-[#FEF3C7] text-[#92400E]",
    paid: "bg-[#DCFCE7] text-[#15803D]",
    granted: "bg-[#DCFCE7] text-[#15803D]",
    revoked: "bg-[#FFE4E6] text-[#BE123C]",
    status_changed: "bg-[#FEF3C7] text-[#92400E]",
    income_created: "bg-[#DCFCE7] text-[#15803D]",
    expense_created: "bg-[#FFE4E6] text-[#BE123C]",
    payment_recorded: "bg-[#DCFCE7] text-[#15803D]",
    payment_reversed: "bg-[#FFE4E6] text-[#BE123C]",
  };
  const tone = palette[action] || "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-heading font-semibold capitalize ${tone}`}>
      {action.replace(/_/g, " ")}
    </span>
  );
}

function BranchChip({ name, logo }) {
  if (!name) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-heading font-semibold bg-[#FFE8D6] text-[#9A3412] max-w-[160px]"
      data-testid="activity-branch-chip"
      title={name}
    >
      <span className="w-4 h-4 rounded-full overflow-hidden flex items-center justify-center shrink-0 bg-white/70">
        {logo
          ? <img src={logo} alt="" className="w-full h-full object-cover" />
          : <Building2 className="w-2.5 h-2.5 text-[#9A3412]" />}
      </span>
      <span className="truncate">{name}</span>
    </span>
  );
}

export default function ActivityLogsPage() {
  const { selectedBranch, branchesForSelectedSchool, setSelectedBranchId } = useBranch();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [scope, setScope] = useState("all_branches"); // "all_branches" | "selected_branch"
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [debouncedQ, moduleFilter, actionFilter, scope, selectedBranch?.id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (debouncedQ) params.q = debouncedQ;
      if (moduleFilter !== "all") params.module = moduleFilter;
      if (actionFilter !== "all") params.action = actionFilter;
      if (scope === "selected_branch" && selectedBranch?.id) params.branch_id = selectedBranch.id;
      const { data: res } = await api.get("/activity-logs", { params });
      setData(res || { items: [], total: 0, total_pages: 1 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [page, debouncedQ, moduleFilter, actionFilter, scope, selectedBranch?.id]);

  useEffect(() => { load(); }, [load]);

  // Load module breakdown once
  useEffect(() => {
    api.get("/activity-logs/modules").then(({ data }) => setModules(data || [])).catch(() => {});
  }, []);

  const goto = (p) => {
    if (p < 1 || p > (data.total_pages || 1)) return;
    setPage(p);
  };

  const clearFilters = () => {
    setQ("");
    setModuleFilter("all");
    setActionFilter("all");
    setScope("all_branches");
  };

  const hasFilter = q || moduleFilter !== "all" || actionFilter !== "all" || scope !== "all_branches";

  return (
    <>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] flex items-center justify-center">
            <ScrollText className="w-6 h-6 text-[#2563EB]" />
          </div>
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-foreground tracking-tight">
              Activity Logs
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Every meaningful action across your schools — login, approvals, payments, edits and deletes.
            </p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <section className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-floating p-4 mb-5" data-testid="activity-filters">
        <div className="grid lg:grid-cols-[1fr,180px,200px,200px] gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search description, who did it, or what changed…"
              className="rounded-2xl h-11 pl-10 border-[#2563EB]/20"
              data-testid="activity-search-input"
            />
          </div>
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="h-11 px-3 rounded-2xl border border-[#2563EB]/20 bg-white text-sm font-heading text-foreground"
            data-testid="activity-module-filter"
          >
            <option value="all">All modules</option>
            {Object.entries(MODULE_STYLES).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="h-11 px-3 rounded-2xl border border-[#2563EB]/20 bg-white text-sm font-heading text-foreground"
            data-testid="activity-action-filter"
          >
            {ACTION_OPTIONS.map((a) => (
              <option key={a} value={a}>{a === "all" ? "All actions" : a.replace(/_/g, " ")}</option>
            ))}
          </select>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="h-11 px-3 rounded-2xl border border-[#2563EB]/20 bg-white text-sm font-heading text-foreground"
            data-testid="activity-scope-filter"
          >
            <option value="all_branches">All branches</option>
            <option value="selected_branch" disabled={!selectedBranch?.id}>
              {selectedBranch?.id ? `Current: ${selectedBranch.name}` : "Pick a branch first"}
            </option>
          </select>
        </div>
        {hasFilter && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="text-slate-500">
              Showing <span className="font-heading font-semibold text-foreground">{data.total}</span> matching {data.total === 1 ? "entry" : "entries"}.
            </span>
            <button
              onClick={clearFilters}
              className="ml-auto inline-flex items-center gap-1 text-[#2563EB] hover:underline font-heading text-xs"
              data-testid="activity-clear-filters"
            >
              <X className="w-3 h-3" /> Clear filters
            </button>
          </div>
        )}
      </section>

      {/* List card */}
      <section
        className="bg-white rounded-3xl border border-[#2563EB]/10 shadow-floating"
        data-testid="activity-list-card"
      >
        {loading ? (
          <p className="p-8 text-slate-500 text-center font-heading">Loading...</p>
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center" data-testid="activity-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#DBEAFE] flex items-center justify-center mb-3">
              <AlertCircle className="w-7 h-7 text-[#2563EB]" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">
              {hasFilter ? "No entries match your filters" : "No activity yet"}
            </p>
            <p className="text-slate-500 text-sm mt-1 max-w-md mx-auto">
              {hasFilter
                ? "Try clearing filters or broadening your search."
                : "Once you start adding students, marking attendance or recording payments, you'll see everything here."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[#DBE7F5]" data-testid="activity-list">
            {data.items.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start gap-4 px-5 py-4 hover:bg-[#EFF6FF]/45 transition-colors"
                data-testid={`activity-row-${entry.id}`}
              >
                <UserAvatar name={entry.actor_name} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-heading font-semibold text-foreground">{entry.actor_name}</span>
                    <span className="text-[11px] text-slate-500 capitalize">{entry.actor_role?.replace("_", " ")}</span>
                    <ModulePill module={entry.module} />
                    <ActionBadge action={entry.action} />
                    <BranchChip name={entry.branch_name} logo={entry.branch_logo_base64} />
                  </div>
                  <p className="text-sm text-foreground leading-snug">{entry.description}</p>
                  {entry.target_name && (
                    <p className="text-xs text-slate-500 mt-1 truncate">Target: {entry.target_name}</p>
                  )}
                </div>
                <div
                  className="text-xs text-slate-500 shrink-0 font-heading tabular-nums whitespace-nowrap"
                  title={new Date(entry.created_at).toLocaleString()}
                >
                  {timeAgo(entry.created_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Pagination */}
      {data.total > 0 && (
        <div className="mt-5 flex items-center justify-between flex-wrap gap-3" data-testid="pagination-bar">
          <p className="text-sm text-slate-500">
            Showing <span className="font-heading font-semibold text-foreground">{Math.min(data.total, (page - 1) * PAGE_SIZE + 1)}–{Math.min(data.total, page * PAGE_SIZE)}</span> of <span className="font-heading font-semibold text-foreground">{data.total}</span>
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline" size="sm"
              onClick={() => goto(page - 1)}
              disabled={page <= 1}
              className="rounded-full border-[#2563EB]/30 text-[#2563EB] hover:bg-[#EFF6FF] disabled:opacity-40"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <span className="px-3 py-1 text-sm font-heading text-slate-500">
              Page <span className="text-foreground font-semibold">{page}</span> of <span className="text-foreground font-semibold">{data.total_pages}</span>
            </span>
            <Button
              variant="outline" size="sm"
              onClick={() => goto(page + 1)}
              disabled={page >= data.total_pages}
              className="rounded-full border-[#2563EB]/30 text-[#2563EB] hover:bg-[#EFF6FF] disabled:opacity-40"
              data-testid="pagination-next"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
